import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Modal, Button, Row, Col, Spinner, Nav, Tab } from 'react-bootstrap'
import { getRutaById } from '@shared/helpers/api/mantenedores/rutas'
import { useSelector } from 'react-redux'
import { SuccessSwal, ErrorSwal } from '@shared/helpers/swal'
import GuiasRutaTable from './GuiasRutaTable'
import BonosRutaTable from './BonosRutaTable'
import EditarRutaModal from './EditarRutaModal'

// Formatear monto con separadores de miles
const formatMonto = monto => {
    if (!monto && monto !== 0) return ''
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(monto)
}

// Calcular total de bonos según la lógica solicitada
const calcularTotalBonos = (bonosArr) => {
    if (!Array.isArray(bonosArr)) return 0
    return bonosArr.reduce((acc, bono) => {
        if (bono.tipo_extra === 'bonos') {
            return acc + (bono.monto || 0)
        } else {
            return acc - (bono.monto || 0)
        }
    }, 0)
}

const RutaModal = ({ show, onHide, rutaId, handleUpdateRutaPrice, onRutaUpdated, permisos }) => {
    const user = useSelector(state => state.auth.user)
    const [ruta, setRuta] = useState(null)
    const [loading, setLoading] = useState(false)
    const [showEditarRutaModal, setShowEditarRutaModal] = useState(false)

    useEffect(() => {
        if (show && rutaId) {
            setLoading(true)
            getRutaById(user, rutaId)
                .then(res => {
                    setRuta(res.success ? res.data : null)
                })
                .catch(() => setRuta(null))
                .finally(() => setLoading(false))
        }
    }, [show, rutaId, user])

    const handleOpenEditarRuta = () => {
        setShowEditarRutaModal(true)
    }

    const handleCloseEditarRuta = () => {
        setShowEditarRutaModal(false)
    }

    const handleRutaUpdated = (rutaActualizada) => {
        setRuta(rutaActualizada)
        setShowEditarRutaModal(false)
        // Notificar al componente padre para cerrar modal y actualizar tabla
        if (onRutaUpdated) {
            onRutaUpdated()
        }
    }

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Detalle de Ruta</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '180px' }}>
                        <Spinner animation="border" />
                    </div>
                ) : ruta ? (
                    <div>
                        <Row className="mb-3">
                            <Col md={4}>
                                <b>Ruta:</b> {ruta.ruta}
                            </Col>
                            <Col md={4}>
                                <b>Fecha:</b> {ruta.fecha}
                            </Col>
                            <Col md={4}>
                                <b>UsuarioSpread:</b> {ruta.usuarioSpread || ruta.conductor?.usuarioSpread || ''}
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={4}>
                                <b>Patente:</b> {ruta.ppu_vehiculo}
                            </Col>
                            <Col md={4}>
                                <b>NS:</b> {ruta.ns} %
                            </Col>
                            <Col md={4}>
                                <b>NsConsolidado:</b> {ruta.ns_consolidado} %
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={4}>
                                <b>TMS:</b> {ruta.tms?.tms}
                            </Col>
                            <Col md={4}>
                                <b>Región:</b> {ruta.regiones?.map(r => r.region).join(', ') || 'N/A'}
                            </Col>
                            {ruta.empresa && (
                                <Col md={4}>
                                    <b>Empresa:</b> {ruta.empresa.razonSocial} ({ruta.empresa.sigla})
                                </Col>
                            )}
                        </Row>
                        <Row className="mb-3">
                            <Col md={4}>
                                <b>Total Guías:</b> {ruta.estadisticas?.total_guias ?? ''}
                            </Col>
                            <Col md={4}>
                                <b>{ruta.tipo_precio === 'fijo' ? 'Precio Fijo:' : 'Precio P*Q:'}</b> {formatMonto(ruta.estadisticas?.precio_total || 0)}
                            </Col>
                            <Col md={2}>
                                <b>Bono NS:</b> {formatMonto(ruta.estadisticas?.pago_ns ?? 0)}
                            </Col>
                            <Col md={2}>
                                <b>Bonos:</b> {formatMonto(calcularTotalBonos(ruta.estadisticas?.bonos) || 0)}
                            </Col>
                        </Row>
                    </div>
                ) : (
                    <div>No se encontró información de la ruta.</div>
                )}
            </Modal.Body>
            {/* Tabla de Guías */}
            <Modal.Body>
                {!loading &&
                ruta &&
                Array.isArray(ruta.guias) &&
                (ruta.guias.length > 0 || ruta.estadisticas?.bonos?.length > 0) ? (
                    <Tab.Container defaultActiveKey="guias">
                        <Nav variant="tabs">
                            <Nav.Item>
                                <Nav.Link eventKey="guias">Guías</Nav.Link>
                            </Nav.Item>
                            {ruta.estadisticas?.bonos && ruta.estadisticas.bonos.length > 0 && (
                                <Nav.Item>
                                    <Nav.Link eventKey="bonos">Bonos</Nav.Link>
                                </Nav.Item>
                            )}
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey="guias">
                                <GuiasRutaTable guias={ruta.guias} />
                            </Tab.Pane>
                            {ruta.estadisticas?.bonos && ruta.estadisticas.bonos.length > 0 && (
                                <Tab.Pane eventKey="bonos">
                                    <BonosRutaTable bonos={ruta.estadisticas.bonos} />
                                </Tab.Pane>
                            )}
                        </Tab.Content>
                    </Tab.Container>
                ) : ruta && Array.isArray(ruta.guias) && ruta.guias.length === 0 ? (
                    <div className="text-center">No hay guías para esta ruta.</div>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                {ruta && (
                    <Button variant="primary" onClick={handleOpenEditarRuta} className="me-2">
                        Editar Ruta
                    </Button>
                )}
                <Button variant="outline-dark" onClick={onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
            
            {/* Modal para editar ruta */}
            {ruta && (
                <EditarRutaModal
                    show={showEditarRutaModal}
                    onHide={handleCloseEditarRuta}
                    ruta={ruta}
                    onRutaUpdated={handleRutaUpdated}
                    handleUpdateRutaPrice={handleUpdateRutaPrice}
                    permisos={permisos}
                />
            )}
        </Modal>
    )
}

RutaModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    rutaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleUpdateRutaPrice: PropTypes.func.isRequired,
    onRutaUpdated: PropTypes.func,
    permisos: PropTypes.object
}

export default RutaModal
